import {BackendFetch} from '@hantera/portal-app'
import parseJson from '@/parse-json'
import {v7 as uuidv7} from 'uuid'
import {
  Client,
  ClientSecret,
  ClientsListParams,
  ClientsListResponse,
  IamApiError,
  Principal,
  PrincipalsListParams,
  PrincipalsListResponse,
  Role,
  RolesListParams,
  RolesListResponse,
  Session,
} from './iam.types'

function handleError(error: any): never {
  if (error.response) {
    const status = error.response.status
    const data = error.response.data

    throw new IamApiError(status, data.code || 'UNKNOWN_ERROR', data.message || error.message)
  }

  if (error instanceof IamApiError) {
    throw error
  }
  console.error(error)
  throw new IamApiError(0, 'NETWORK_ERROR', 'Network error occurred')
}

function principalMethods(fetch: BackendFetch) {
  return {
    async getPrincipals(params?: PrincipalsListParams): Promise<PrincipalsListResponse> {
      try {
        const queryParams = new URLSearchParams()
        if (params?.name) queryParams.set('name', params.name)
        if (params?.email) queryParams.set('email', params.email)
        if (params?.roles?.length) {
          params.roles.forEach((role) => queryParams.append('roles', role))
        }
        if (params?.includeSuspended !== undefined) {
          queryParams.set('includeSuspended', params.includeSuspended.toString())
        }
        if (params?.after) queryParams.set('after', params.after)
        if (params?.before) queryParams.set('before', params.before)
        if (params?.limit) queryParams.set('limit', params.limit.toString())
        if (params?.orderBy) queryParams.set('orderBy', params.orderBy)

        const response = await fetch(`/iam/principals?${queryParams}`, {
          method: 'GET',
        })

        if (!response.ok) {
          throw new IamApiError(response.status, 'FETCH_FAILED', `Failed to fetch principals`)
        }

        const data = parseJson(await response.text())
        return data
      } catch (error) {
        handleError(error)
      }
    },

    async getPrincipal(id: string): Promise<Principal> {
      try {
        const response = await fetch(`/iam/principals/${id}`, {
          method: 'GET',
        })

        if (!response.ok) {
          if (response.status === 404) {
            throw new IamApiError(404, 'NOT_FOUND', 'Principal not found')
          }
          throw new IamApiError(response.status, 'FETCH_FAILED', 'Failed to fetch principal')
        }

        const data = parseJson(await response.text())
        return data
      } catch (error) {
        handleError(error)
      }
    },

    async createPrincipal(data: {
      name: string
      email: string
      phone?: string
      roles?: string[]
      permissions?: string[]
    }): Promise<Principal> {
      try {
        // Generate new UUID for principal
        const id = uuidv7()

        // Use first-class properties (consistent with PATCH endpoint)
        const body = {
          name: data.name,
          email: data.email,
          phone: data.phone,
          roles: data.roles || [],
          acl: data.permissions || [],
          accessAttributes: {},
          // no etag for creation
        }

        const response = await fetch(`/iam/principals/${id}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        })

        if (!response.ok) {
          var { errors } = await response.json() as { errors: { code: string, message?: string }[] }
          if (errors.length > 0) {
            throw new IamApiError(response.status, errors[0].code, errors[0].message)
          }
          throw new IamApiError(response.status, 'CREATE_FAILED', 'Failed to create principal')
        }

        const result = parseJson(await response.text())
        return result
      } catch (error) {
        handleError(error)
      }
    },

    async updatePrincipal(
      id: string,
      updates: {
        name?: string
        email?: string
        phone?: string
        picture?: string
        settings?: Record<string, any>
      },
      etag?: string,
    ): Promise<void> {
      try {
        const body: Record<string, any> = { ...updates }
        if (etag) {
          body.etag = etag
        }

        const response = await fetch(`/iam/principals/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        })

        if (!response.ok) {
          throw new IamApiError(response.status, 'UPDATE_FAILED', 'Failed to update principal')
        }
      } catch (error) {
        handleError(error)
      }
    },

    async deletePrincipal(id: string): Promise<void> {
      try {
        const response = await fetch(`/iam/principals/${id}`, {
          method: 'DELETE',
        })

        if (!response.ok) {
          throw new IamApiError(response.status, 'DELETE_FAILED', 'Failed to delete principal')
        }
      } catch (error) {
        handleError(error)
      }
    },

    async suspendPrincipal(id: string): Promise<Principal> {
      try {
        const response = await fetch(`/iam/principals/${id}/suspend`, {
          method: 'POST',
        })

        if (!response.ok) {
          throw new IamApiError(response.status, 'SUSPEND_FAILED', 'Failed to suspend principal')
        }

        const data = parseJson(await response.text())
        return data
      } catch (error) {
        handleError(error)
      }
    },

    async reactivatePrincipal(id: string): Promise<Principal> {
      try {
        const response = await fetch(`/iam/principals/${id}/reactivate`, {
          method: 'POST',
        })

        if (!response.ok) {
          throw new IamApiError(
            response.status,
            'REACTIVATE_FAILED',
            'Failed to reactivate principal',
          )
        }

        const data = parseJson(await response.text())
        return data
      } catch (error) {
        handleError(error)
      }
    },

    async resetPassword(
      id: string,
      sendEmail: boolean,
    ): Promise<{ temporaryPassword?: string; sendingId?: string }> {
      try {
        const response = await fetch(`/iam/principals/${id}/password/reset`, {
          method: 'POST',
          body: JSON.stringify({ sendEmail }),
        })

        if (!response.ok) {
          throw new IamApiError(
            response.status,
            'RESET_PASSWORD_FAILED',
            'Failed to reset password',
          )
        }

        const data = parseJson(await response.text())
        return data
      } catch (error) {
        handleError(error)
      }
    },

    async getPrincipalSessions(id: string): Promise<Session[]> {
      try {
        const response = await fetch(`/iam/principals/${id}/sessions`, {
          method: 'GET',
        })

        if (!response.ok) {
          throw new IamApiError(response.status, 'FETCH_SESSIONS_FAILED', 'Failed to fetch sessions')
        }

        const data = parseJson(await response.text())
        return Array.isArray(data) ? data : (data.sessions || [])
      } catch (error) {
        handleError(error)
      }
    },

    async revokePrincipalSession(principalId: string, sessionId: string): Promise<void> {
      try {
        const response = await fetch(
          `/iam/principals/${principalId}/sessions/${sessionId}`,
          {
            method: 'DELETE',
          },
        )

        if (!response.ok) {
          throw new IamApiError(
            response.status,
            'REVOKE_SESSION_FAILED',
            'Failed to revoke session',
          )
        }
      } catch (error) {
        handleError(error)
      }
    },

    async revokeAllPrincipalSessions(id: string): Promise<void> {
      try {
        const response = await fetch(`/iam/principals/${id}/sessions`, {
          method: 'DELETE',
        })

        if (!response.ok) {
          throw new IamApiError(
            response.status,
            'REVOKE_ALL_SESSIONS_FAILED',
            'Failed to revoke all sessions',
          )
        }
      } catch (error) {
        handleError(error)
      }
    },

    async updatePrincipalPermissions(id: string, acl: string[], etag?: string): Promise<Principal> {
      try {
        const headers: Record<string, string> = {}
        if (etag) {
          headers['If-Match'] = etag
        }

        const response = await fetch(`/iam/principals/${id}/acl`, {
          method: 'PUT',
          body: JSON.stringify({ acl, etag }),
          headers,
        })

        if (!response.ok) {
          throw new IamApiError(
            response.status,
            'UPDATE_ACL_FAILED',
            'Failed to update ACL',
          )
        }

        const data = parseJson(await response.text())
        return data
      } catch (error) {
        handleError(error)
      }
    },

    async updatePrincipalRoles(id: string, roles: string[], etag?: string): Promise<Principal> {
      try {
        const headers: Record<string, string> = {}
        if (etag) {
          headers['If-Match'] = etag
        }

        const response = await fetch(`/iam/principals/${id}/roles`, {
          method: 'PUT',
          body: JSON.stringify({ roles, etag }),
          headers,
        })

        if (!response.ok) {
          throw new IamApiError(
            response.status,
            'UPDATE_ROLES_FAILED',
            'Failed to update roles',
          )
        }

        const data = parseJson(await response.text())
        return data
      } catch (error) {
        handleError(error)
      }
    },

    async addPrincipalRole(id: string, roleName: string): Promise<void> {
      try {
        const response = await fetch(
          `/iam/principals/${id}/roles/${encodeURIComponent(roleName)}`,
          {
            method: 'PUT',
          },
        )

        if (!response.ok) {
          throw new IamApiError(response.status, 'ADD_ROLE_FAILED', 'Failed to add role')
        }
      } catch (error) {
        handleError(error)
      }
    },

    async removePrincipalRole(id: string, roleName: string): Promise<void> {
      try {
        const response = await fetch(
          `/iam/principals/${id}/roles/${encodeURIComponent(roleName)}`,
          {
            method: 'DELETE',
          },
        )

        if (!response.ok) {
          throw new IamApiError(response.status, 'REMOVE_ROLE_FAILED', 'Failed to remove role')
        }
      } catch (error) {
        handleError(error)
      }
    },

    async findPrincipalByEmail(email: string): Promise<Principal | null> {
      try {
        const response = await fetch(
          `/iam/principals/by-email/${encodeURIComponent(email)}`,
          {
            method: 'GET',
          },
        )

        if (response.status === 404) {
          return null
        }

        if (!response.ok) {
          throw new IamApiError(response.status, 'FETCH_FAILED', 'Failed to find principal')
        }

        const data = parseJson(await response.text())
        return data
      } catch (error) {
        handleError(error)
      }
    },

    async getPrincipalsWithRole(roleName: string): Promise<Principal[]> {
      try {
        const response = await fetch(
          `/iam/principals?role=${encodeURIComponent(roleName)}`,
          {
            method: 'GET',
          },
        )

        if (!response.ok) {
          throw new IamApiError(response.status, 'FETCH_FAILED', 'Failed to fetch principals')
        }

        const data = parseJson(await response.text())
        return data.principals || []
      } catch (error) {
        handleError(error)
      }
    },
  }
}

function clientMethods(fetch: BackendFetch) {
  return {
    async getClients(params?: ClientsListParams): Promise<ClientsListResponse> {
      try {
        const queryParams = new URLSearchParams()
        if (params?.type) queryParams.set('type', params.type)
        if (params?.includeSuspended !== undefined) {
          queryParams.set('includeSuspended', params.includeSuspended.toString())
        }
        if (params?.after) queryParams.set('after', params.after)
        if (params?.before) queryParams.set('before', params.before)
        if (params?.limit) queryParams.set('limit', params.limit.toString())
        if (params?.orderBy) queryParams.set('orderBy', params.orderBy)

        const response = await fetch(`/iam/clients?${queryParams}`, {
          method: 'GET',
        })

        if (!response.ok) {
          throw new IamApiError(response.status, 'FETCH_FAILED', 'Failed to fetch clients')
        }

        const data = parseJson(await response.text())
        return data
      } catch (error) {
        handleError(error)
      }
    },

    async getClient(id: string): Promise<Client> {
      try {
        const response = await fetch(`/iam/clients/${id}`, {
          method: 'GET',
        })

        if (!response.ok) {
          if (response.status === 404) {
            throw new IamApiError(404, 'NOT_FOUND', 'Client not found')
          }
          throw new IamApiError(response.status, 'FETCH_FAILED', 'Failed to fetch client')
        }

        const data = parseJson(await response.text())
        return data
      } catch (error) {
        handleError(error)
      }
    },

    async createClient(data: {
      name: string
      description?: string
      type?: string
      redirectUrls?: string[]
      grantTypes?: string[]
      roles?: string[]
      permissions?: string[]
    }): Promise<Client> {
      try {
        // Generate new UUID for client
        const id = uuidv7()

        // ClientCreateOrUpdateBody uses first-class properties
        const body = {
          name: data.name,
          description: data.description,
          redirectUris: data.redirectUrls,
          grantTypes: data.grantTypes,
          scopes: data.permissions,
          roles: data.roles || [],
          acl: [],
          accessAttributes: {},
          // no etag for creation
        }

        const response = await fetch(`/iam/clients/${id}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        })

        if (!response.ok) {
          var { errors } = await response.json() as { errors: { code: string, message?: string }[] }
          if (errors.length > 0) {
            throw new IamApiError(response.status, errors[0].code, errors[0].message)
          }
          throw new IamApiError(response.status, 'CREATE_FAILED', 'Failed to create client')
        }

        const result = parseJson(await response.text())
        return result
      } catch (error) {
        handleError(error)
      }
    },

    async updateClient(
      id: string,
      updates: {
        name?: string
        description?: string
        redirectUris?: string[]
        grantTypes?: string[]
        scopes?: string[]
      },
      etag?: string,
    ): Promise<void> {
      try {
        const body: Record<string, any> = { ...updates }
        if (etag) {
          body.etag = etag
        }

        const response = await fetch(`/iam/clients/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        })

        if (!response.ok) {
          throw new IamApiError(response.status, 'UPDATE_FAILED', 'Failed to update client')
        }
      } catch (error) {
        handleError(error)
      }
    },

    async deleteClient(id: string): Promise<void> {
      try {
        const response = await fetch(`/iam/clients/${id}`, {
          method: 'DELETE',
        })

        if (!response.ok) {
          throw new IamApiError(response.status, 'DELETE_FAILED', 'Failed to delete client')
        }
      } catch (error) {
        handleError(error)
      }
    },

    async generateClientSecret(
      id: string,
      options?: { description?: string; expiresAt?: Date }
    ): Promise<{ secretId: string; secret: string }> {
      try {
        const body = options ? {
          description: options.description,
          expiresAt: options.expiresAt?.toISOString()
        } : undefined

        const response = await fetch(`/iam/clients/${id}/secrets`, {
          method: 'POST',
          body: body ? JSON.stringify(body) : undefined,
        })

        if (!response.ok) {
          throw new IamApiError(
            response.status,
            'GENERATE_SECRET_FAILED',
            'Failed to generate secret',
          )
        }

        const data = parseJson(await response.text())
        return data
      } catch (error) {
        handleError(error)
      }
    },

    async getClientSecrets(id: string): Promise<ClientSecret[]> {
      try {
        const response = await fetch(`/iam/clients/${id}/secrets`, {
          method: 'GET',
        })

        if (!response.ok) {
          throw new IamApiError(response.status, 'FETCH_SECRETS_FAILED', 'Failed to fetch secrets')
        }

        const data = parseJson(await response.text())
        return Array.isArray(data) ? data : []
      } catch (error) {
        handleError(error)
      }
    },

    async revokeClientSecret(clientId: string, secretId: string): Promise<void> {
      try {
        const response = await fetch(
          `/iam/clients/${clientId}/secrets/${secretId}`,
          {
            method: 'DELETE',
          },
        )

        if (!response.ok) {
          throw new IamApiError(
            response.status,
            'REVOKE_SECRET_FAILED',
            'Failed to revoke secret',
          )
        }
      } catch (error) {
        handleError(error)
      }
    },

    async getClientSessions(id: string): Promise<Session[]> {
      try {
        const response = await fetch(`/iam/clients/${id}/sessions`, {
          method: 'GET',
        })

        if (!response.ok) {
          throw new IamApiError(response.status, 'FETCH_SESSIONS_FAILED', 'Failed to fetch sessions')
        }

        const data = parseJson(await response.text())
        return data.sessions || []
      } catch (error) {
        handleError(error)
      }
    },

    async revokeClientSession(clientId: string, sessionId: string): Promise<void> {
      try {
        const response = await fetch(
          `/iam/clients/${clientId}/sessions/${sessionId}`,
          {
            method: 'DELETE',
          },
        )

        if (!response.ok) {
          throw new IamApiError(
            response.status,
            'REVOKE_SESSION_FAILED',
            'Failed to revoke session',
          )
        }
      } catch (error) {
        handleError(error)
      }
    },

    async updateClientAcl(id: string, acl: string[], etag?: string): Promise<Client> {
      try {
        const headers: Record<string, string> = {}
        if (etag) {
          headers['If-Match'] = etag
        }

        const response = await fetch(`/iam/clients/${id}/acl`, {
          method: 'PUT',
          body: JSON.stringify({ acl, etag }),
          headers,
        })

        if (!response.ok) {
          throw new IamApiError(
            response.status,
            'UPDATE_ACL_FAILED',
            'Failed to update ACL',
          )
        }

        const data = parseJson(await response.text())
        return data
      } catch (error) {
        handleError(error)
      }
    },

    async updateClientRoles(id: string, roles: string[], etag?: string): Promise<Client> {
      try {
        const headers: Record<string, string> = {}
        if (etag) {
          headers['If-Match'] = etag
        }

        const response = await fetch(`/iam/clients/${id}/roles`, {
          method: 'PUT',
          body: JSON.stringify({ roles, etag }),
          headers,
        })

        if (!response.ok) {
          throw new IamApiError(
            response.status,
            'UPDATE_ROLES_FAILED',
            'Failed to update roles',
          )
        }

        const data = parseJson(await response.text())
        return data
      } catch (error) {
        handleError(error)
      }
    },

    async addClientRole(id: string, roleName: string): Promise<void> {
      try {
        const response = await fetch(
          `/iam/clients/${id}/roles/${encodeURIComponent(roleName)}`,
          {
            method: 'PUT',
          },
        )

        if (!response.ok) {
          throw new IamApiError(response.status, 'ADD_ROLE_FAILED', 'Failed to add role')
        }
      } catch (error) {
        handleError(error)
      }
    },

    async removeClientRole(id: string, roleName: string): Promise<void> {
      try {
        const response = await fetch(
          `/iam/clients/${id}/roles/${encodeURIComponent(roleName)}`,
          {
            method: 'DELETE',
          },
        )

        if (!response.ok) {
          throw new IamApiError(response.status, 'REMOVE_ROLE_FAILED', 'Failed to remove role')
        }
      } catch (error) {
        handleError(error)
      }
    },

    async getClientsWithRole(roleName: string): Promise<Client[]> {
      try {
        const response = await fetch(
          `/iam/clients?role=${encodeURIComponent(roleName)}`,
          {
            method: 'GET',
          },
        )

        if (!response.ok) {
          throw new IamApiError(response.status, 'FETCH_FAILED', 'Failed to fetch clients')
        }

        const data = parseJson(await response.text())
        return data.clients || []
      } catch (error) {
        handleError(error)
      }
    },
  }
}

function roleMethods(fetch: BackendFetch) {
  return {
    async getRoles(params?: RolesListParams): Promise<RolesListResponse> {
      try {
        const queryParams = new URLSearchParams()
        if (params?.includeSystem !== undefined) {
          queryParams.set('includeSystem', params.includeSystem.toString())
        }
        if (params?.after) queryParams.set('after', params.after)
        if (params?.before) queryParams.set('before', params.before)
        if (params?.limit) queryParams.set('limit', params.limit.toString())
        if (params?.sortBy) queryParams.set('sortBy', params.sortBy)
        if (params?.sortDirection) queryParams.set('sortDirection', params.sortDirection)

        const response = await fetch(`/iam/roles?${queryParams}`, {
          method: 'GET',
        })

        if (!response.ok) {
          throw new IamApiError(response.status, 'FETCH_FAILED', 'Failed to fetch roles')
        }

        const data = parseJson(await response.text())
        return data
      } catch (error) {
        handleError(error)
      }
    },

    async getRole(id: string): Promise<Role> {
      try {
        const response = await fetch(`/iam/roles/${id}`, {
          method: 'GET',
        })

        if (!response.ok) {
          if (response.status === 404) {
            throw new IamApiError(404, 'NOT_FOUND', 'Role not found')
          }
          throw new IamApiError(response.status, 'FETCH_FAILED', 'Failed to fetch role')
        }

        const data = await response.json()
        return data
      } catch (error) {
        handleError(error)
      }
    },

    async createRole(
      key: string,
      description?: string
    ): Promise<Role> {
      try {

        const response = await fetch(`/iam/roles/${key}`, {
          method: 'PUT',
          body: JSON.stringify({ description }),
        })

        if (!response.ok) {
          const { errors } = await response.json() as { errors: { code: string, message?: string }[] }
          if (errors.length > 0) {
            throw new IamApiError(response.status, errors[0].code, errors[0].message)
          }
          throw new IamApiError(response.status, 'CREATE_FAILED', 'Failed to create role')
        }

        const result = parseJson(await response.text())
        return result
      } catch (error) {
        handleError(error)
      }
    },

    async updateRole(
      id: string,
      updates: {
        description?: string
      },
      etag?: string,
    ): Promise<void> {
      try {
        const body: Record<string, any> = { ...updates }
        if (etag) {
          body.etag = etag
        }

        const response = await fetch(`/iam/roles/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        })

        if (!response.ok) {
          throw new IamApiError(response.status, 'UPDATE_FAILED', 'Failed to update role')
        }
      } catch (error) {
        handleError(error)
      }
    },

    async updateRoleAcl(id: string, acl: string[], etag?: string): Promise<Role> {
      try {
        const headers: Record<string, string> = {}
        if (etag) {
          headers['If-Match'] = etag
        }

        const response = await fetch(`/iam/roles/${id}/acl`, {
          method: 'PUT',
          body: JSON.stringify({ acl, etag }),
          headers,
        })

        if (!response.ok) {
          throw new IamApiError(
            response.status,
            'UPDATE_ACL_FAILED',
            'Failed to update ACL',
          )
        }

        const data = parseJson(await response.text())
        return data
      } catch (error) {
        handleError(error)
      }
    },

    async deleteRole(id: string): Promise<void> {
      try {
        const response = await fetch(`/iam/roles/${id}`, {
          method: 'DELETE',
        })

        if (!response.ok) {
          throw new IamApiError(response.status, 'DELETE_FAILED', 'Failed to delete role')
        }
      } catch (error) {
        handleError(error)
      }
    },

    async getRoleMembers(
      roleKey: string,
    ): Promise<{ principals: Principal[]; clients: Client[] }> {
      try {
        const response = await fetch(
          `/iam/roles/${encodeURIComponent(roleKey)}/members`,
          {
            method: 'GET',
          },
        )

        if (!response.ok) {
          throw new IamApiError(response.status, 'FETCH_MEMBERS_FAILED', 'Failed to fetch members')
        }

        const data = parseJson(await response.text())
        return {
          principals: data.principals || [],
          clients: data.clients || [],
        }
      } catch (error) {
        handleError(error)
      }
    },
  }
}

export default function iam(fetch: BackendFetch) {
  return {
    ...principalMethods(fetch),
    ...clientMethods(fetch),
    ...roleMethods(fetch),
  }
}
